import React, { useEffect, useMemo, useState } from "react";
import { getDocumentation } from "../../../services/analytics/analyticsService";
import { DocumentationResponse } from "../../../interfaces/analytics";
import AnalyticsFilters from "./AnalyticsFilters";
import Pagination from "../utility/Pagination";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  ResponsiveContainer,
} from "recharts";

/**
 * Modern, insight‑forward replacement for the old pie chart view.
 *
 * Key upgrades
 * - KPI cards w/ compact context
 * - Trend area (if timeseries present)
 * - Distribution bar (if buckets present)
 * - Actionable table w/ search + sort + pagination
 * - Graceful fallbacks if data shape is partial
 */

interface Props {
  authToken: string;
}

// Lightweight type guards so we render only what exists
function hasTimeseries(d: DocumentationResponse | null): d is DocumentationResponse & { timeseries: Array<{ day: string; completePct: number; }> } {
  // @ts-ignore — tolerate unknown shapes
  return !!d && Array.isArray((d as any).timeseries);
}

function hasBuckets(d: DocumentationResponse | null): d is DocumentationResponse & { buckets: Array<{ name: string; count: number; }>; } {
  // @ts-ignore
  return !!d && Array.isArray((d as any).buckets);
}

const EmptyState: React.FC<{ message?: string }>=({ message = "No data available for the selected filters." }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
    <div className="text-4xl">📊</div>
    <p className="text-muted-foreground max-w-prose">{message}</p>
  </div>
);

const DocumentationAnalytics: React.FC<Props> = ({ authToken }) => {
  const [data, setData] = useState<DocumentationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // filters
  const [filters, setFilters] = useState<any>({});

  // table controls
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<"name" | "owner" | "updated">("updated");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const res = await getDocumentation(authToken, filters);
        if (!cancelled) setData(res as DocumentationResponse);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [authToken, filters]);

  const completionPct = Math.max(0, Math.min(100, (data as any)?.documentationCompletePct ?? 0));
  const missingRows = (data as any)?.missing ?? [] as Array<any>;

  // derived table rows
  const filteredRows = useMemo(() => {
    let rows = missingRows as Array<any>;
    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter((r) =>
        [r?.name, r?.owner, r?.updated]?.some((v) => (v + "").toLowerCase().includes(q))
      );
    }
    rows = rows.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = (a?.[sortKey] ?? "") + "";
      const bv = (b?.[sortKey] ?? "") + "";
      if (sortKey === "updated") {
        return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      }
      return av.localeCompare(bv) * dir;
    });
    return rows;
  }, [missingRows, query, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));
  const pageRows = filteredRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // ring dims
  const radius = 54;
  const circ = 2 * Math.PI * radius;
  const dash = (completionPct / 100) * circ;

  if (loading) return <div className="p-6 text-center">Loading documentation analytics…</div>;
  if (error) return <div className="p-6 text-center text-red-500">{error}</div>;
  if (!data) return <EmptyState />;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white/50 dark:bg-neutral-900/50 backdrop-blur rounded-2xl p-4 shadow-sm border border-neutral-200/60 dark:border-neutral-800">
      <AnalyticsFilters filters={filters} onFiltersChange={setFilters} />
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Completion ring */}
        <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm p-5 bg-white dark:bg-neutral-900">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-300">Documentation completion</h3>
            <span className="text-xs px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800">% complete</span>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <svg width={140} height={140} viewBox="0 0 140 140" className="shrink-0">
              <circle cx="70" cy="70" r={radius} className="opacity-20" stroke="currentColor" strokeWidth="12" fill="none" />
              <circle
                cx="70"
                cy="70"
                r={radius}
                stroke="currentColor"
                strokeWidth="12"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${circ}`}
                transform="rotate(-90 70 70)"
              />
            </svg>
            <div>
              <div className="text-4xl font-semibold leading-none">{completionPct.toFixed(0)}%</div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{100 - completionPct}% remaining</p>
            </div>
          </div>
        </div>

        {/* Totals */}
        <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm p-5 bg-white dark:bg-neutral-900">
          <div className="text-sm text-neutral-600 dark:text-neutral-300">Missing documents</div>
          <div className="text-4xl font-semibold mt-2">{missingRows.length}</div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">Items that still need attention</p>
        </div>

        {/* Last updated hint */}
        <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm p-5 bg-white dark:bg-neutral-900">
          <div className="text-sm text-neutral-600 dark:text-neutral-300">Data source</div>
          <div className="text-lg mt-1">Documentation Analytics</div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">Auto‑refreshes when filters change</p>
        </div>
      </div>

      {/* Trends & distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Trend over time */}
        <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm p-5 bg-white dark:bg-neutral-900 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Completion trend</h3>
            <span className="text-xs text-neutral-500">By day</span>
          </div>
          {hasTimeseries(data) && data.timeseries.length > 0 ? (
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.timeseries} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="fillA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopOpacity={0.35} />
                      <stop offset="100%" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v: any) => `${v}%`} labelFormatter={(l: string) => `Day: ${l}`} />
                  <Area type="monotone" dataKey="completePct" fill="url(#fillA)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="No timeseries available for this view." />
          )}
        </div>

        {/* Current distribution */}
        <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm p-5 bg-white dark:bg-neutral-900">
          <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-200">By category</h3>
          {hasBuckets(data) && data.buckets.length > 0 ? (
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.buckets} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis dataKey="name" interval={0} angle={-30} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="No categorical breakdown provided." />
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div>
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Missing documentation</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Search, sort, and triage what to fix next</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={query}
              onChange={(e) => { setCurrentPage(1); setQuery(e.target.value); }}
              placeholder="Search name, owner, date…"
              className="px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/70 focus:outline-none"
            />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as any)}
              className="ps-3 pe-5 py-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/70"
            >
              <option value="updated">Sort by: Updated</option>
              <option value="name">Sort by: Name</option>
              <option value="owner">Sort by: Owner</option>
            </select>
            <button
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/70"
            >
              {sortDir === "asc" ? "↑" : "↓"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left bg-neutral-50 dark:bg-neutral-800/50">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium">Updated</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-neutral-500">No matching rows</td>
                </tr>
              )}
              {pageRows.map((r: any, idx: number) => (
                <tr key={`${r?.id ?? r?.name ?? idx}`} className="border-t border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50/60 dark:hover:bg-neutral-800/40">
                  <td className="px-4 py-3">
                    <div className="font-medium">{r?.name ?? "—"}</div>
                    {r?.path && <div className="text-neutral-500 text-xs mt-0.5 truncate max-w-[42ch]">{r.path}</div>}
                  </td>
                  <td className="px-4 py-3">{r?.owner ?? "—"}</td>
                  <td className="px-4 py-3">{r?.updated ? new Date(r.updated).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      Needs doc
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-3 border-t border-neutral-100 dark:border-neutral-800">
          <Pagination
            currentPage={currentPage}
            totalPages={pageCount}
            onPageChange={(n: number) => setCurrentPage(n)}
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentationAnalytics;
